import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  alreadyLocalized,
  normalizeContentLocale,
  translateText,
} from '../../../services/contentTranslation';

/**
 * Translate a single text block for display when locale is not English.
 * Skips translation while `isStreaming` is true (content still arriving).
 */
export function useTranslatedContent(
  text: string,
  cacheId: string,
  options?: {
    enabled?: boolean;
    isStreaming?: boolean;
    context?: string;
  },
): { text: string; isTranslating: boolean } {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const normalized = normalizeContentLocale(locale);
  const enabled = options?.enabled !== false;
  const isStreaming = options?.isStreaming === true;
  const context = options?.context ?? 'agent conversation';

  const [translated, setTranslated] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (!enabled || !text.trim() || normalized === 'en' || isStreaming || alreadyLocalized(text, normalized)) {
      setTranslated(null);
      setIsTranslating(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setIsTranslating(true);
    void translateText(cacheId, locale, text, context, controller.signal)
      .then((result) => {
        if (!cancelled) setTranslated(result);
      })
      .catch(() => {
        if (!cancelled) setTranslated(null);
      })
      .finally(() => {
        if (!cancelled) setIsTranslating(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [cacheId, context, enabled, isStreaming, locale, normalized, text]);

  return {
    text: translated ?? text,
    isTranslating: isTranslating && normalized !== 'en',
  };
}
